import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { css } from '@emotion/react';
import axios from 'axios';

import type { Organization } from '@hotjar/organization';
import {
  tokens,
  TextInput,
  Field,
  Stack,
  Button,
  Alert,
  IconButton,
  Text,
  Select,
  useNotify,
} from '@hotjar/hotsauce';
import { validateEmail } from '@hotjar/webapp/lib/email';
import type { Site } from '@hotjar/site';
import { Trans, useTranslation } from '@hotjar/translations';
import { ModalSearchResultInfo } from './ModalSearchResultInfo';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x3};
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x3};
`;

const DataSubjectWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${tokens.spacer.x1};
`;

const DataSubjectField = styled(Field)`
  flex-grow: 1;
`;

interface FormResult {
  dataSubjectEmail: string;
  dataSubject: Array<{ siteId: string; userId: string }>;
}

interface RequestByAppSectionProps {
  organization: Organization;
  hasLookupCreatePermission: boolean;
  currentUserDataEmail: string;
  sites: Site[];
}

const DATA_SUBJECT_EMAIL = 'dataSubjectEmail';
const DATA_SUBJECT = 'dataSubject';

export const RequestByAppSection = ({
  organization,
  hasLookupCreatePermission,
  currentUserDataEmail,
  sites,
}: RequestByAppSectionProps): JSX.Element => {
  const { t } = useTranslation('common');
  const { showToast } = useNotify();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState(false);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const { control, register, handleSubmit, errors, getValues, reset, watch } =
    useForm<FormResult>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: DATA_SUBJECT,
  });

  const dataSubject = watch('dataSubject');
  const usedSitedIds = dataSubject?.filter((item) => item.siteId).map((item) => +item.siteId) ?? [];

  const resetLookupModel = useCallback(() => {
    reset();
    setIsRequestSubmitted(false);
    setIsRequestPending(false);
  }, [reset]);

  useEffect(() => {
    resetLookupModel();
  }, [organization, resetLookupModel]);

  const onSubmit = () => {
    setIsRequestSubmitted(false);
    setIsRequestPending(false);
    setIsModalOpen(true);
  };

  const getSelectableSites = (selectedSiteId?: string) => {
    const selectableSites = (sites ?? []).filter((oneSite: Site) => {
      return (
        (selectedSiteId && +oneSite.id === +selectedSiteId) || !usedSitedIds.includes(oneSite.id)
      );
    });
    return selectableSites;
  };

  const submitLookupRequest = async () => {
    try {
      const path = `api/v1/accounts/${organization.accountId}/organizations/${organization.id}/visitor-lookup/requests`;
      const values = getValues();
      const dataSubjects = (values.dataSubject ?? [])
        .filter((oneDataSubject) => {
          return oneDataSubject.siteId && oneDataSubject.userId;
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .reduce((acc: any, oneDataSubject) => {
          acc[oneDataSubject.siteId] = (acc[oneDataSubject.siteId] || []).concat(
            oneDataSubject.userId,
          );
          return acc;
        }, {});
      const requestBody = {
        data_subject: values.dataSubjectEmail,
        data_subject_site_id_to_user_ids: dataSubjects,
      };
      const response = await axios.post(path, requestBody);
      const { data } = response;
      setIsRequestPending(false);
      if (data.success) {
        setIsRequestSubmitted(true);
      } else if (data.error_target === 'data_subject') {
        setIsRequestPending(true);
      }
    } catch (e) {
      showToast({ text: t('error.default-message'), kind: 'danger' });
    }
  };

  const submitNewRequest = () => {
    resetLookupModel();
  };

  return (
    <ContentWrapper>
      <Text as="h2" textType="header5">
        {t('visitor-lookup.request-by-app-section.title')}
      </Text>
      <Text as="p" textType="body2">
        {t('visitor-lookup.request-by-app-section.description', {
          organizationName: organization.name,
        })}
      </Text>

      {(!isRequestSubmitted || isRequestPending) && (
        <StyledForm name="lookupForm" id="lookupForm" onSubmit={handleSubmit(onSubmit)}>
          {!hasLookupCreatePermission && (
            <Alert kind="warning">
              <Trans
                i18nKey="common:visitor-lookup.request-by-app-section.permission-needed"
                components={[<strong />]}
                values={{ organizationName: organization.name }}
              />
            </Alert>
          )}
          {hasLookupCreatePermission && (
            <Alert kind="info">
              <Trans
                components={[<strong />]}
                i18nKey="common:visitor-lookup.request-by-app-section.info-message"
              />
            </Alert>
          )}

          <Stack preset="form">
            <Field
              inputId={DATA_SUBJECT_EMAIL}
              labelText={t(
                'visitor-lookup.request-by-app-section.data-subject-email-label',
              ).toUpperCase()}
              helperText={t('visitor-lookup.request-by-app-section.data-subject-email-helper')}
              isInvalid={!!errors.dataSubjectEmail}
              invalidText={errors.dataSubjectEmail?.message}
              isRequired
              css={css`
                width: 100%;
              `}
            >
              <TextInput
                id={DATA_SUBJECT_EMAIL}
                name={DATA_SUBJECT_EMAIL}
                placeholder={t(
                  'visitor-lookup.request-by-app-section.data-subject-email-placeholder',
                )}
                isInvalid={!!errors.dataSubjectEmail}
                isDisabled={!hasLookupCreatePermission}
                ref={
                  register &&
                  register({
                    required: t(
                      'visitor-lookup.request-by-app-section.data-subject-email-enter-valid-email-address',
                    ),
                    validate: (value: string) =>
                      validateEmail(value) ||
                      t(
                        'visitor-lookup.request-by-app-section.data-subject-email-enter-valid-email-address',
                      ),
                  })
                }
                autoComplete="off"
              />
            </Field>
            <Field
              inputId={DATA_SUBJECT}
              labelText={t('visitor-lookup.request-by-app-section.data-subject-userid-label')}
              helperText={t('visitor-lookup.request-by-app-section.data-subject-userid-helper')}
              css={css`
                width: 100%;
              `}
            >
              {fields.map((field, index) => {
                return (
                  <DataSubjectWrapper key={field.id}>
                    <DataSubjectField
                      inputId={`${DATA_SUBJECT}[${index}].siteId`}
                      labelText=""
                      isInvalid={!!errors.dataSubject?.[index]?.siteId}
                      invalidText={errors.dataSubject?.[index]?.siteId?.message}
                    >
                      <Controller
                        name={`${DATA_SUBJECT}[${index}].siteId`}
                        control={control}
                        rules={{
                          required: t(
                            'visitor-lookup.request-by-app-section.data-subject-userid-select-site',
                          ),
                        }}
                        render={({ onChange, value, ref }) => (
                          <Select
                            id={`${DATA_SUBJECT}_${index}_siteId`}
                            name={`${DATA_SUBJECT}[${index}].siteId`}
                            isDisabled={!hasLookupCreatePermission}
                            ref={ref}
                            isInvalid={!!errors?.dataSubject?.[index]?.siteId}
                            value={value}
                            onChange={onChange}
                          >
                            <option value="">
                              {t(
                                'visitor-lookup.request-by-app-section.data-subject-userid-default-option',
                              )}
                            </option>
                            {getSelectableSites(getValues(`${DATA_SUBJECT}[${index}].siteId`)).map(
                              (oneSite: Site) => (
                                <option value={oneSite.id} key={oneSite.id}>
                                  {oneSite.name}
                                </option>
                              ),
                            )}
                          </Select>
                        )}
                      />
                    </DataSubjectField>
                    <DataSubjectField
                      inputId={`${DATA_SUBJECT}[${index}].userId`}
                      labelText=""
                      isInvalid={!!errors.dataSubject?.[index]?.userId}
                      invalidText={errors.dataSubject?.[index]?.userId?.message}
                    >
                      <TextInput
                        placeholder={t(
                          'visitor-lookup.request-by-app-section.data-subject-userid-placeholder',
                        )}
                        id={`${DATA_SUBJECT}_${index}_userId`}
                        name={`${DATA_SUBJECT}[${index}].userId`}
                        ref={register({
                          required: t(
                            'visitor-lookup.request-by-app-section.data-subject-userid-enter-userid',
                          ),
                        })}
                        isInvalid={!!errors?.dataSubject?.[index]?.userId}
                        isDisabled={!hasLookupCreatePermission}
                        autoComplete="off"
                      />
                    </DataSubjectField>

                    <IconButton
                      id={`${DATA_SUBJECT}_${index}_delete_btn`}
                      kind="ghost"
                      tooltipText={t(
                        'visitor-lookup.request-by-app-section.data-subject-userid-delete',
                      )}
                      icon="trash"
                      isDisabled={!hasLookupCreatePermission}
                      onClick={() => remove(index)}
                      size="m"
                      css={css`
                        margin-top: ${tokens.spacer.x1};
                      `}
                    />
                  </DataSubjectWrapper>
                );
              })}
              {fields.length < (sites ?? []).length && (
                <Button
                  kind="ghost"
                  id={`${DATA_SUBJECT}_add_new_btn`}
                  icon="plus"
                  isDisabled={!hasLookupCreatePermission}
                  onClick={() =>
                    append({
                      siteId: undefined,
                      userId: undefined,
                    })
                  }
                  css={css`
                    margin-top: ${tokens.spacer.x1};
                  `}
                >
                  {fields.length === 0
                    ? t('visitor-lookup.request-by-app-section.data-subject-userid-specify-userid')
                    : t(
                        'visitor-lookup.request-by-app-section.data-subject-userid-specify-other-userid',
                      )}
                </Button>
              )}
            </Field>
            {isRequestPending && (
              <Alert id="error_pending_subject" kind="danger">
                {t('visitor-lookup.request-by-app-section.request-pending')}
              </Alert>
            )}
          </Stack>
          <div>
            <Button
              id="submit_btn"
              kind="primary"
              isDisabled={!hasLookupCreatePermission}
              type="submit"
            >
              {t('visitor-lookup.request-by-app-section.submit-request')}
            </Button>
          </div>
        </StyledForm>
      )}
      <ModalSearchResultInfo
        setOpen={setIsModalOpen}
        isOpen={isModalOpen}
        onSubmit={submitLookupRequest}
      />

      {isRequestSubmitted && (
        <>
          <Alert id="alert_request_successful" kind="success">
            {t('visitor-lookup.request-by-app-section.request-submitted', { currentUserDataEmail })}
          </Alert>

          <div>
            <Button
              id="new_request_btn"
              isDisabled={!hasLookupCreatePermission}
              type="button"
              kind="secondary"
              onClick={submitNewRequest}
            >
              {t('visitor-lookup.request-by-app-section.submit-new-request')}
            </Button>
          </div>
        </>
      )}
    </ContentWrapper>
  );
};
